import {
	CombinedGraphQLErrors,
	ServerError,
	ServerParseError,
} from "@apollo/client";
import * as R from "ramda";

/**
 * The transaction id jasper-api puts on a GraphQL error's extensions.
 *
 * Apollo Client 4 removed `ApolloError` and its `graphQLErrors` array: an
 * operation that fails with GraphQL errors rejects with one
 * `CombinedGraphQLErrors` carrying them as `errors`.
 */
export const parseTransactionIdFromGraphQLErrors = R.compose(
	R.path(["extensions", "transactionID"]),
	R.defaultTo({}),
	R.find(R.hasPath(["extensions", "transactionID"])),
	(error) => (CombinedGraphQLErrors.is(error) ? error.errors : []),
);

const graphQLErrorsReducer = (acc, { message, path }) => `${acc}
Message: ${message}, Path: ${path}`;

export const formatGraphQLErrors = (graphQLErrors) => {
	const PREFIX = "Apollo error > GraphQL error >";
	try {
		return new Error(graphQLErrors.reduce(graphQLErrorsReducer, PREFIX));
	} catch {
		return new Error(
			`${PREFIX} Unknown type - a graphQL error occured, but could not be correctly logged. Expected array of errors but got ${typeof graphQLErrors} for ${graphQLErrors}`,
		);
	}
};

export const formatNetworkError = (networkError) => {
	const formattedError = new Error(
		`Apollo error > Network error > Original error message: ${networkError.message}`,
	);

	formattedError.originalError = networkError;

	return formattedError;
};

/**
 * Under Apollo Client 3 a network failure arrived wrapped in an `ApolloError`
 * as `networkError`. Apollo Client 4 rejects with the failure itself: a
 * `ServerError` or `ServerParseError` from the HTTP link, or the `TypeError`
 * `fetch` throws when the request never gets a response.
 */
const isNetworkError = (error) =>
	ServerError.is(error) ||
	ServerParseError.is(error) ||
	error instanceof TypeError;

export const formatIfApolloError = (rawError) => {
	if (CombinedGraphQLErrors.is(rawError) && rawError.errors.length) {
		return formatGraphQLErrors(rawError.errors);
	}

	if (isNetworkError(rawError)) {
		return formatNetworkError(rawError);
	}

	return rawError;
};
